import { ContainerComponent, wrap, Wrapper, today } from 'AppReact';
import { Ml2, Mt2, Mb1, FlexAlignEndWrapper, BgWhiteWrapper, GenericWrapper, FlexBoxWrapper, PreventPointerEventWrapper } from 'wrappers';
import { InputError, FullWidthInput } from 'molecules';
import { ClickableIcon, SecondaryTextXs } from 'atoms';
import { TickSvg, ModalCloseButton } from 'components/icons';
import styled from 'styled-components';
import { LabeledDatepicker, Dropdown } from 'containers';
import { calculateHoursBetweenDates, generateTimeArray } from 'utils/dateAndTimeUtils';
import { format } from 'date-fns';
import internalState from './internalState';
import controller from './controller';

const options = generateTimeArray();

const LabelText = wrap(wrap(SecondaryTextXs, FlexBoxWrapper), Mb1);

export const definition = ({
  controller,
  name: 'CreateUpdateTimer',
  initialProps: ({ techs, techId, showOnTop, startDateTime, endDateTime, event_id, order_id, request_id }) => {
    const startDateTimeOriginal = startDateTime ? new Date(startDateTime) : today();
    const endDateTimeOriginal = endDateTime ? new Date(endDateTime) : today();
    const { total: totalHours } = calculateHoursBetweenDates({ startDate: startDateTimeOriginal, endDate: endDateTimeOriginal, withSeconds: true });
    const startTime = format(startDateTimeOriginal, 'hh:mm aa');
    const endTime = format(endDateTimeOriginal, 'hh:mm aa');
    return ({ techs: techs || [], techId, startTime, payloadStartDateTime: startDateTimeOriginal, payloadEndDateTime: endDateTimeOriginal, endTime, startDateTimeOriginal, endDateTimeOriginal, showOnTop, order_id, request_id, event_id, totalHours });
  },
  props: { ...internalState },
  elements: [
    {
      wrapper: wrap(FlexAlignEndWrapper, Mt2),
      name: 'ContainerWrapper',
      elements: [
        {
          wrapper: Wrapper(styled.div`display: flex; gap: 5px; flex-wrap: wrap;`),
          name: 'InputsWrapper',
          elements: [
            {
              wrapper: wrap(GenericWrapper, Mt2),
              name: 'StartWrapper',
              elements: [
                {
                  component: LabelText,
                  name: 'Label',
                  displayValue: () => 'Start'
                },
                {
                  component: wrap(Dropdown, BgWhiteWrapper),
                  name: 'StartDropdown',
                  bindings: ({ startTime, endTime, startDateTimeOriginal, endDateTimeOriginal, payloadStartDateTime, payloadEndDateTime }) => ({ startTime, endTime, startDateTimeOriginal, endDateTimeOriginal, disableLabel: true, enableFullWidth: true, options, label: '', search: true, selectedOptionId: startTime, selectedDate: payloadStartDateTime, payloadEndDateTime, payloadStartDateTime })
                },
                {
                  component: wrap(LabeledDatepicker, BgWhiteWrapper),
                  name: 'Calendar',
                  bindings: ({ showOnTop, startDateTimeOriginal, endDateTimeOriginal, startTime, endTime, payloadStartDateTime, payloadEndDateTime }) => ({
                    endTime,
                    showOnTop,
                    startTime,
                    startDateTimeOriginal,
                    endDateTimeOriginal,
                    payloadEndDateTime,
                    enableFullWidth: true,
                    disabledAfter: today(),
                    selectedDate: payloadStartDateTime ? new Date(payloadStartDateTime) : new Date(),
                    label: {
                      values: [''],
                      keys: ['text'],
                      component: SecondaryTextXs
                    }
                  })
                }
              ]
            },
            {
              wrapper: wrap(GenericWrapper, Mt2),
              name: 'EndWrapper',
              elements: [
                {
                  component: LabelText,
                  name: 'Label',
                  displayValue: () => 'End'
                },
                {
                  component: wrap(Dropdown, BgWhiteWrapper),
                  name: 'EndDropdown',
                  bindings: ({ startTime, endTime, startDateTimeOriginal, endDateTimeOriginal, payloadEndDateTime, payloadStartDateTime }) => ({ startTime, endTime, startDateTimeOriginal, endDateTimeOriginal, disableLabel: true, enableFullWidth: true, options, label: '', search: true, selectedOptionId: endTime, selectedDate: payloadEndDateTime, payloadEndDateTime, payloadStartDateTime })
                },
                {
                  component: wrap(LabeledDatepicker, BgWhiteWrapper),
                  name: 'Calendar',
                  bindings: ({ showOnTop, startDateTimeOriginal, endDateTimeOriginal, startTime, endTime, payloadEndDateTime, payloadStartDateTime }) => ({
                    endTime,
                    showOnTop,
                    startTime,
                    startDateTimeOriginal,
                    endDateTimeOriginal,
                    payloadStartDateTime,
                    payloadEndDateTime,
                    enableFullWidth: true,
                    disabledAfter: today(),
                    selectedDate: payloadEndDateTime ? new Date(payloadEndDateTime) : new Date(),
                    label: {
                      values: [''],
                      keys: ['text'],
                      component: SecondaryTextXs
                    }
                  })
                }
              ]
            },
            {
              component: wrap(Dropdown, Mt2),
              name: 'Tech',
              renderIf: ({ techs }) => techs.length > 0,
              bindings: ({ techs, techId }) => {
                const options = techs.map((t) => ({ display: t.name, id: t.id }));
                return { label: 'Tech', nullable: true, options, selectedOptionId: techId };
              }
            },
            {
              wrapper: wrap(GenericWrapper, Mt2),
              name: 'TotalWrapper',
              elements: [
                {
                  component: LabelText,
                  name: 'Label',
                  displayValue: () => 'Total'
                },
                {
                  component: FullWidthInput,
                  name: 'TotalHours',
                  bindings: ({ startTime, endTime, startDateTimeOriginal, endDateTimeOriginal, totalHours, payloadStartDateTime, payloadEndDateTime }) => ({ startTime, endTime, startDateTimeOriginal, endDateTimeOriginal, label: '', totalHours, value: totalHours ?? '', payloadStartDateTime, payloadEndDateTime })
                }
              ]
            }
          ]
        },
        {
          wrapper: wrap(GenericWrapper, Ml2),
          name: 'Buttons',
          elements: [
            {
              component: ClickableIcon,
              name: 'Submit',
              renderIf: ({ isDirty }) => isDirty,
              displayValue: ({ }) => TickSvg({ circle: true, width: 20, height: 20, color: window?.settings?.primary_color ?? '#1DBD6B' }),
              bindings: ({
                order_id,
                request_id,
                event_id,
                totalHours,
                selectedDate,
                startTime,
                endTime,
                techId,
                startDateTimeOriginal,
                endDateTimeOriginal,
                payloadStartDateTime,
                payloadEndDateTime
              }) => ({
                action: 'update_timer_event',
                payload: {
                  order_id,
                  request_id,
                  event_id,
                  startTime,
                  endTime,
                  from: payloadStartDateTime,
                  to: payloadEndDateTime,
                  tech_id: techId,
                  totalHours,
                  selectedDate: selectedDate ?? new Date(),
                  startDateTimeOriginal,
                  endDateTimeOriginal
                }
              })
            },
            {
              component: wrap(ClickableIcon, PreventPointerEventWrapper),
              name: 'Submit',
              renderIf: ({ isDirty }) => !isDirty,
              bindings: () => ({}),
              displayValue: ({ }) => TickSvg({ circle: true, width: 20, height: 20, color: '#9AA0A6' })
            },
            {
              component: ClickableIcon,
              name: 'CloseButton',
              bindings: () => ({}),
              displayValue: () => ModalCloseButton({ disableborderHover: true, width: 23, height: 23, xSize: 2, xColor: 'white', fill: '#ff0505' })
            }
          ]
        }
      ]
    },
    {
      component: InputError,
      name: 'TotalHoursError',
      renderIf: ({ totalHoursError }) => !!totalHoursError,
      bindings: ({ totalHoursError }) => ({ error: totalHoursError ?? '' })
    }
  ]
});

export default ContainerComponent(definition);
